
import { useState, useEffect } from "react";
import { db, auth } from "../../../firebaseClient";
import { useToast } from "../../components/Toast";
import type { LegalModuleConfig } from "./types";
import { contractsMapping } from "./mappings/contracts";
import { canViewData } from "../../utils/dataAccess";
import { getRoleByEmail, isReadOnlyUser } from "../../config/roles";

const MAPPINGS: Record<string, any> = {
  "contracts": contractsMapping,
};

function getCurrentAppUser() {
    const u = auth.currentUser;
    if (!u) return null;
    const email = u.email || "";
    return {
        id: u.uid,
        email: email,
        name: u.displayName || "",
        role: getRoleByEmail(email),
        displayName: u.displayName || "",
        isReadOnly: isReadOnlyUser(email)
    };
}

export function useLegalCrudModule(config: LegalModuleConfig) {
  const [items, setItems] = useState<any[]>([]);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchText, setSearchText] = useState("");
  const [showLegacy, setShowLegacy] = useState(false);
  
  const { showToast } = useToast();

  useEffect(() => {
    setLoading(true);
    // Basic collection fetch, sort later
    const col = db.collection(config.collection);
    
    const unsubscribe = col.onSnapshot(
      (snap) => {
        let rawList: any[] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        
        // Manual Sort desc
        rawList.sort((a, b) => {
            const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
            const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
            return tb - ta;
        });

        const currentUser = getCurrentAppUser();
        
        // Security Filter
        const visibleList = rawList.filter(item => canViewData(currentUser, item));
        
        setItems(visibleList);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error(`[LegalModule] Error fetching ${config.collection}`, err);
        setError(err);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [config.collection]);

  useEffect(() => {
    let result = items;

    // Filter Logic for Legacy
    if (!showLegacy) {
        result = result.filter(i => !i.isLegacy);
    }

    if (searchText) {
        const lower = searchText.toLowerCase();
        result = result.filter(item => {
          return config.searchableFields.some(field => {
            const val = item[field];
            return val && String(val).toLowerCase().includes(lower);
          });
        });
    }
    
    setFilteredItems(result);
  }, [items, searchText, config.searchableFields, showLegacy]);

  const handleDelete = async (id: string, row: any) => {
    if (!confirm("確定要刪除此筆資料嗎？")) return;
    
    try {
      const mapping = config.exportMapping ? MAPPINGS[config.exportMapping] : null;
      if (mapping && mapping.deleteFn) {
        await mapping.deleteFn(id, row);
      } else {
        await db.collection(config.collection).doc(id).delete();
      }
      showToast("已刪除資料");
    } catch (e: any) {
      console.error(e);
      showToast("刪除失敗: " + e.message, "error");
    }
  };

  const handleImport = async (file: File) => {
    const mapping = config.importMapping ? MAPPINGS[config.importMapping] : null;
    if (!mapping || !mapping.importFn) {
      alert("此模組尚未設定匯入功能");
      return;
    }
    try {
      const result = await mapping.importFn(file);
      showToast(`匯入成功: 新增 ${result.created}, 更新 ${result.updated}`);
    } catch (e: any) {
      console.error(e);
      showToast("匯入失敗: " + e.message, "error");
    }
  };

  const handleExport = async () => {
    const mapping = config.exportMapping ? MAPPINGS[config.exportMapping] : null;
    if (!mapping || !mapping.exportFn) {
      alert("此模組尚未設定匯出功能");
      return;
    }
    try {
      const csvContent = await mapping.exportFn(filteredItems);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${config.id}_export_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e: any) {
      console.error(e);
      showToast("匯出失敗: " + e.message, "error");
    }
  };

  return {
    items,
    filteredItems,
    loading,
    error,
    searchText,
    setSearchText,
    showLegacy,
    setShowLegacy,
    handleDelete,
    handleImport,
    handleExport
  };
}
